// Import GSAP and ScrollTrigger
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

// Make GSAP globally available for inline scripts
if (!window.gsap) {
  window.gsap = gsap;
}
if (!window.ScrollTrigger) {
  window.ScrollTrigger = ScrollTrigger;
}

// Optimize GSAP for better performance
gsap.config({
  force3D: true,
  nullTargetWarn: false
});

gsap.ticker.lagSmoothing(0);

function villeHero() {
  console.log(performance.now());
  
  // ----- CONFIG -----
  const frameCount = 239; // Total number of images in sequence

  // Determine which responsive image folder to use based on screen size
  function getImagePath() {
    const width = window.innerWidth;
    
    if (width < 650) {
      return 'heroBanner/optimized_1024';
    } else if (width < 1024) {
      return 'heroBanner/optimized_1480';
    } else if (width < 1480) {
      return 'heroBanner/optimized_1920';
    } else if (width < 1920) {
      return 'heroBanner/optimized_2240';
    } else {
      return 'heroBanner/optimized_2240';
    }
  }

  // Function to get image source for a specific frame
  function getImageSrc(frameIndex) {
    const path = getImagePath();
    const paddedNumber = frameIndex.toString().padStart(2, '0');
    return `https://acron.ams3.digitaloceanspaces.com/ville/${path}/acrontown${paddedNumber}.webp`;
  }

  // ----- DOM & SAFETY -----
  function ready(fn) {
    document.readyState !== 'loading'
      ? fn()
      : document.addEventListener('DOMContentLoaded', fn, { once: true });
  }

  ready(() => {
    const heroContainer = document.getElementById('hero');
    const fallbackImg = document.getElementById('hero-fallback');
    
    if (!heroContainer) {
      console.error('Hero container #hero not found.');
      return;
    }

    // Start preloading images immediately when page loads
    function startPreloading() {
      preloadImages();
    }

    // Wait for ticker to finish before showing animation
    function waitForTickerToFinish() {
      const hero = document.querySelector('#hero');
      if (hero && hero.__x && hero.__x.$data && hero.__x.$data.loading) {
        setTimeout(waitForTickerToFinish, 100);
        return;
      }
      
      initializeImageSequence();
    }

    // Preload all images before starting animation
    function preloadImages() {
      let loadedCount = 0;
      const imagesToPreload = [];
      const imageCache = {}; // Cache for loaded images
      
      // Preload images in batches
      for (let i = 0; i < frameCount; i++) {
        const img = new Image();
        img.src = getImageSrc(i);
        imagesToPreload.push(img);
        imageCache[i] = img; // Store in cache
        
        img.onload = () => {
          loadedCount++;
          const progress = Math.round((loadedCount / frameCount) * 100);
          // console.log(`VilleBanner: Loaded ${loadedCount}/${frameCount} images (${progress}%)`);
          
          // When all images are loaded
          if (loadedCount === frameCount) {
            console.log('Hero loaded', performance.now());
            // Store the cache globally so scroll animation can use it
            window.heroImageCache = imageCache;
            waitForTickerToFinish();
          }
        };
        
        img.onerror = () => {
          loadedCount++;
          
          // Continue even if some images fail
          if (loadedCount === frameCount) {
            console.log('Hero loaded', performance.now());
            window.heroImageCache = imageCache;
            waitForTickerToFinish();
          }
        };
      }
    }

    // Initialize the image sequence
    function initializeImageSequence() {
      // Create single image element for sequence
      createSequenceImage();
      
      // Hide fallback and show sequence
      showImageSequence();
      
      // Start scroll animation
      startScrollAnimation();
      
      // Dispatch ready event after hero is fully loaded and initialized
      console.log('VilleBanner: Hero fully loaded and initialized, dispatching villeBannerReady event');
      window.dispatchEvent(new CustomEvent('villeBannerReady'));
    }

    // Create the canvas element for the sequence
    function createSequenceImage() {
      // Remove any existing sequence image
      const existingSequence = document.getElementById('hero-sequence-img');
      if (existingSequence) existingSequence.remove();

      // Create canvas element instead of img to prevent network requests
      const canvas = document.createElement('canvas');
      canvas.id = 'hero-sequence-img';
      canvas.className = 'absolute inset-0 z-0 object-cover object-bottom w-full h-full';
      canvas.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center bottom;
        opacity: 0;
        transition: opacity 0.5s ease-in;
      `;
      canvas.alt = 'Hero sequence animation';

      // Insert after fallback image
      if (fallbackImg) {
        fallbackImg.parentNode.insertBefore(canvas, fallbackImg.nextSibling);
      } else {
        heroContainer.insertBefore(canvas, heroContainer.firstChild);
      }
    }

    // Show the image sequence and hide fallback
    function showImageSequence() {
      const canvas = document.getElementById('hero-sequence-img');
      
      // Hide fallback image
      if (fallbackImg) {
        fallbackImg.style.opacity = '0';
        fallbackImg.style.transition = 'opacity 0.5s ease-out';
      }
      
      // Show canvas and draw first frame
      if (canvas && window.heroImageCache && window.heroImageCache[0]) {
        canvas.style.opacity = '1';
        canvas.style.transition = 'opacity 0.5s ease-in';
        
        // Draw the first frame to canvas
        const ctx = canvas.getContext('2d');
        const firstFrame = window.heroImageCache[0];
        if (firstFrame.complete) {
          canvas.width = firstFrame.naturalWidth;
          canvas.height = firstFrame.naturalHeight;
          ctx.drawImage(firstFrame, 0, 0);
        }
      }
    }

    // Start GSAP scroll animation
    function startScrollAnimation() {
      if (!window.gsap || !window.ScrollTrigger) {
        console.error('GSAP/ScrollTrigger not loaded.');
        return;
      }
      
      gsap.registerPlugin(ScrollTrigger);

      const hero = document.querySelector('#hero');
      const villeBody = document.querySelector('#ville-body');
      if (!hero) return;

      const canvas = document.getElementById('hero-sequence-img');
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');

      const cloud = hero.querySelector('[cloud-animate]');
      const cloudScrollHeight = window.innerHeight / 2;
      const cloudScrollEnd = window.innerHeight;
      
      // Cloud parallax animation
      if (cloud) {
        gsap.to(cloud, {
          y: -cloudScrollHeight,
          scrollTrigger: {
            trigger: hero,
            start: 'top top',
            end: () => `+=${cloudScrollEnd}px`,
            scrub: true
          }
        });
      }

      // Main image sequence animation - this is the key part!
      const current = { frame: 0 };
      
      gsap.to(current, {
        frame: frameCount - 1,
        ease: 'none',
        snap: 'frame',
        scrollTrigger: {
          trigger: hero,
          start: () => `bottom-=${window.innerHeight * 0.5}px bottom`,
          end: () => `+=${window.innerHeight * 0.3 + 500}px`, // Reduced end distance to finish sequence earlier
          scrub: 0.05,
          invalidateOnRefresh: true,
          onUpdate: () => {
            // Calculate which frame to show based on scroll progress
            const frame = Math.round(current.frame);
            if (frame >= 0 && frame < frameCount && window.heroImageCache && window.heroImageCache[frame]) {
              // Draw cached image to canvas instead of changing src to prevent network requests
              if (frame !== canvas._lastFrame) {
                canvas._lastFrame = frame;
                const cachedImg = window.heroImageCache[frame];
                if (cachedImg.complete) {
                  canvas.width = cachedImg.naturalWidth;
                  canvas.height = cachedImg.naturalHeight;
                  ctx.drawImage(cachedImg, 0, 0);
                }
              }
            }
          },
          onRefresh: () => {
            // Force update on refresh using cached images
            const frame = Math.round(current.frame);
            if (frame >= 0 && frame < frameCount && window.heroImageCache && window.heroImageCache[frame]) {
              canvas._lastFrame = frame;
              const cachedImg = window.heroImageCache[frame];
              if (cachedImg.complete) {
                canvas.width = cachedImg.naturalWidth;
                canvas.height = cachedImg.naturalHeight;
                ctx.drawImage(cachedImg, 0, 0);
              }
            }
          },
        },
      });
      
      // Hero pinning
      gsap.to(hero, {
        scrollTrigger: {
          trigger: hero,
          start: () => `bottom-=${window.innerHeight * 0.2}px bottom`, // Start pinning earlier
          end: '+=800px', // Reduced end distance
          scrub: 0.05,
          anticipatePin: 1,
          pin: true,
          invalidateOnRefresh: true,
          onUpdate: self => {
            villeBody.style.transform = `translateY(${window.innerHeight * (1 - self.progress)}px)`
          }
        },
      });
    }

    // Handle responsive path changes
    function handleResize() {
      const sequenceImg = document.getElementById('hero-sequence-img');
      
      if (sequenceImg) {
        // Update current frame with new path
        const currentFrame = Math.round(gsap.getProperty(gsap.getById('imageSequenceAnimation'), 'frame') || 0);
        sequenceImg.src = getImageSrc(currentFrame);
      }
      
      // Refresh ScrollTrigger
      if (window.ScrollTrigger) {
        ScrollTrigger.refresh();
      }
    }

    // Resize handler
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        handleResize();
      }, 100);
    });

    // Start preloading images immediately
    startPreloading();
  });
}

villeHero();